/**
 * Dashboard Service
 * Handles dashboard metrics, performance, trading signals, distribution, and header API calls
 */

import httpClient, { extractData } from './http-client';

export interface DashboardHeaderData {
  unreadNotifications: number;
  allowedTabs: string[];
}
import {
  ApiResponse,
  DashboardMetrics,
  PerformanceHistory,
  PerformanceTabResponse,
  TradingSignalsResponse,
  UniverseTabResponse,
  Period,
  SignalPeriod,
  CrossFilterResponse,
  MarketSummary,
  AllBreakdowns,
  ScatterPoint,
} from '../types';

export type DistributionDimension = 'sector' | 'rating' | 'profile' | 'size' | 'country';

class DashboardService {
  /**
   * Get dashboard header data (unread notifications count, etc.)
   */
  async getHeader(): Promise<DashboardHeaderData> {
    const response = await httpClient.get<ApiResponse<DashboardHeaderData>>('/dashboard/header');
    return extractData(response);
  }

  /**
   * Get market summary statistics (includes country, profile, currency distributions)
   */
  async getSummary(): Promise<MarketSummary> {
    const response = await httpClient.get<ApiResponse<MarketSummary>>('/bonds/summary');
    return extractData(response);
  }

  /**
   * Get latest dashboard KPI metrics
   */
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    const response = await httpClient.get<ApiResponse<DashboardMetrics>>(
      '/bonds/dashboard-metrics'
    );
    return extractData(response);
  }

  /**
   * Get portfolio performance time series (CB, Equity, Delta-Neutral)
   */
  async getPerformanceHistory(period: Period = 'YTD'): Promise<PerformanceHistory> {
    const response = await httpClient.get<ApiResponse<PerformanceHistory>>(
      '/bonds/performance-history',
      { params: { period } }
    );
    return extractData(response);
  }

  /**
   * Get performance tab payload: rankings (CB/Equity/DN top-worst 5), implied vol, implied spread, delta-neutral 1D.
   * Backend-only; no local fallback.
   */
  async getPerformanceTab(
    period: Period = 'YTD',
    impliedVolPeriod: Period = '1D',
    impliedSpreadPeriod: Period = '1D'
  ): Promise<PerformanceTabResponse> {
    const response = await httpClient.get<ApiResponse<PerformanceTabResponse>>(
      '/bonds/performance-tab',
      { params: { period, impliedVolPeriod, impliedSpreadPeriod } }
    );
    return extractData(response);
  }

  /**
   * Get Universe tab payload (bond list, optional selected bond detail).
   * Backend-only; used for Universe page dropdown and future full integration.
   */
  async getUniverseTab(
    options?: { bond?: string; period?: '1D' | 'MTD' | '3MTD' | 'YTD'; takeoverPremium?: number; bidDate?: string }
  ): Promise<UniverseTabResponse> {
    const params: Record<string, string> = { period: options?.period || 'YTD' };
    if (options?.bond) params.bond = options.bond;
    if (options?.takeoverPremium != null) params.takeoverPremium = String(options.takeoverPremium);
    if (options?.bidDate) params.bidDate = options.bidDate;
    const response = await httpClient.get<ApiResponse<UniverseTabResponse>>(
      '/bonds/universe-tab',
      { params }
    );
    return extractData(response);
  }

  /**
   * Get trading signals and analysis
   */
  async getTradingSignals(period: SignalPeriod = '10D'): Promise<TradingSignalsResponse> {
    const response = await httpClient.get<ApiResponse<TradingSignalsResponse>>(
      '/bonds/trading-signals',
      { params: { period } }
    );
    return extractData(response);
  }

  /**
   * Get distribution by dimension using cross-filter endpoint
   * Returns current snapshot of portfolio breakdown by sector, rating, profile, or size
   */
  async getDistribution(dimension: DistributionDimension): Promise<CrossFilterResponse> {
    const response = await httpClient.get<ApiResponse<CrossFilterResponse>>(
      '/bonds/aggregations/cross-filter',
      { params: { primary: dimension } }
    );
    return extractData(response);
  }

  /**
   * Get all distributions for dashboard charts in a single call
   * Returns sector, rating, profile, size, and country distributions
   */
  async getAllDistributions(): Promise<{
    sector: CrossFilterResponse;
    rating: CrossFilterResponse;
    profile: CrossFilterResponse;
    size: CrossFilterResponse;
    summary: MarketSummary;
  }> {
    const [sector, rating, profile, size, summary] = await Promise.all([
      this.getDistribution('sector'),
      this.getDistribution('rating'),
      this.getDistribution('profile'),
      this.getDistribution('size'),
      this.getSummary(),
    ]);

    return { sector, rating, profile, size, summary };
  }

  /**
   * Get all breakdowns at global max date (VBA methodology)
   * Single source for sector, rating, credit bucket, country, maturity, size, profile charts
   */
  async getBreakdowns(): Promise<AllBreakdowns> {
    const response = await httpClient.get<ApiResponse<AllBreakdowns>>(
      '/bonds/aggregations/breakdowns'
    );
    return extractData(response);
  }

  /**
   * Get scatter data for CB Strike vs CB Market Price chart.
   * Single backend call (GET /bonds/scatter-data) – no per-bond history requests.
   */
  async getScatterData(limit = 200): Promise<ScatterPoint[]> {
    const response = await httpClient.get<ApiResponse<ScatterPoint[]>>(
      '/bonds/scatter-data',
      { params: { limit } }
    );
    return extractData(response);
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
